import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { getAlllanguages } from "../services/api/apiClient";
import { allLanguages } from "../utils/allLanguages";

type LoadStatus = "idle" | "loading" | "loaded" | "error";

export type LanguageInfo = {
  name: string;
  flag: string;
  langugageCode: string;
};

export type LanguageCodeResponse = {
  languageCode: string;
};

type State = {
  languages: LanguageInfo[];
  portalDesignLanguages: LanguageInfo[];
  selectedLanguage: string;
  loadStatus: LoadStatus;
  // Remembers, per portal, the language the user last explicitly picked for it
  // this session, so switching back to a portal restores that choice instead
  // of resetting to the portal's configured default.
  portalLanguagePreferences: Record<string, string>;
};

type Action =
  | { type: "SET_LANGUAGES"; payload: LanguageInfo[] }
  | { type: "SET_SELECTED_LANGUAGE"; payload: string }
  | { type: "SET_PORTAL_DESIGN_LANGUAGES"; payload: LanguageInfo[] }
  | {
      type: "SET_PORTAL_LANGUAGE_PREFERENCE";
      payload: { portalUid: string; languageCode: string };
    }
  | { type: "REMOVE_LOADER" }
  | { type: "SHOW_LOADER" }
  | {
      type: "SET_LOAD_STATUS";
      payload: LoadStatus;
    };

const initialState: State = {
  languages: [],
  portalDesignLanguages: [],
  selectedLanguage: "en",
  loadStatus: "idle",
  portalLanguagePreferences: {},
};

const LanguagesContext = createContext<{
  languages: State;
  dispatch: React.Dispatch<Action>;
  getLanguageDetails: (langugageCode: string) => LanguageInfo | undefined;
  getLanguageCodeByName: (
    name: string,
    languagesList: LanguageInfo[],
  ) => string | undefined;
  fetchLanguages: () => void;
}>({
  languages: initialState,
  dispatch: () => null,
  getLanguageDetails: () => undefined,
  getLanguageCodeByName: () => undefined,
  fetchLanguages: () => {},
});

const codeOverrides: Record<string, { name: string; flag: string }> = {
  en: { name: "English", flag: "https://flagcdn.com/gb.svg" },
  es: { name: "Spanish", flag: "https://flagcdn.com/es.svg" },
  fr: { name: "French", flag: "https://flagcdn.com/fr.svg" },
  de: { name: "German", flag: "https://flagcdn.com/de.svg" },
  pt: { name: "Portuguese", flag: "https://flagcdn.com/pt.svg" },
  zh: { name: "Chinese", flag: "https://flagcdn.com/cn.svg" },
  ar: { name: "Arabic", flag: "https://flagcdn.com/sa.svg" },
  hi: { name: "Hindi", flag: "https://flagcdn.com/in.svg" },
  ru: { name: "Russian", flag: "https://flagcdn.com/ru.svg" },
  ja: { name: "Japanese", flag: "https://flagcdn.com/jp.svg" },
  it: { name: "Italian", flag: "https://flagcdn.com/it.svg" },
  mk: { name: "Macedonian", flag: "https://flagcdn.com/mk.svg" },
};

export const mapLanguageCodesToInfo = (
  codes: LanguageCodeResponse[],
): LanguageInfo[] => {
  return codes
    .map(({ languageCode }) => {
      const lowerCode = languageCode.toLowerCase();
      const override = codeOverrides[lowerCode];
      if (override) {
        return {
          name: override.name,
          flag: override.flag,
          langugageCode: lowerCode,
        };
      }
      const match = allLanguages.find(
        (lang) => lang.langugageCode.toLowerCase() === lowerCode,
      );
      if (match) {
        return {
          name: match.name,
          flag: match.flag,
          langugageCode: lowerCode,
        };
      }
      return null;
    })
    .filter(Boolean) as LanguageInfo[];
};

const languagesReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_LANGUAGES":
      return { ...state, languages: action.payload };
    case "SET_SELECTED_LANGUAGE":
      localStorage.setItem("selectedLanguage", action.payload);
      return { ...state, selectedLanguage: action.payload };
    case "SET_PORTAL_DESIGN_LANGUAGES":
      return { ...state, portalDesignLanguages: action.payload };
    case "SET_PORTAL_LANGUAGE_PREFERENCE": {
      const portalLanguagePreferences = {
        ...state.portalLanguagePreferences,
        [action.payload.portalUid]: action.payload.languageCode,
      };
      return { ...state, portalLanguagePreferences };
    }
    case "REMOVE_LOADER":
      return { ...state, loadStatus: "loaded" };
    case "SHOW_LOADER":
      return { ...state, loadStatus: "loading" };
    case "SET_LOAD_STATUS": {
      return {
        ...state,
        loadStatus: action.payload,
      };
    }
    default:
      return state;
  }
};

export const LanguagesProvider = ({ children }: { children: ReactNode }) => {
  const [languages, dispatch] = useReducer(languagesReducer, initialState);

  const fetchLanguages = useCallback(async () => {
    try {
      dispatch({
        type: "SET_LOAD_STATUS",
        payload: "loading",
      });
      const codes: LanguageCodeResponse[] = await getAlllanguages();
      const finalLanguages = mapLanguageCodesToInfo(codes);

      dispatch({ type: "SET_LANGUAGES", payload: finalLanguages });
    } catch (err) {
      console.error("Failed to fetch languages:", err);
    } finally {
      dispatch({
        type: "SET_LOAD_STATUS",
        payload: "loaded",
      });
    }
  }, []);

  const getLanguageDetails = useCallback(
    (langugageCode: string) => {
      return languages.languages.find(
        (lang) =>
          lang.langugageCode.toLowerCase() === langugageCode.toLowerCase(),
      );
    },
    [languages.languages],
  );

  const getLanguageCodeByName = useCallback(
    (name: string, languagesList: LanguageInfo[]): string | undefined => {
      const match = languagesList.find(
        (lang) => lang.name.toLowerCase() === name.toLowerCase(),
      );
      return match?.langugageCode;
    },
    [],
  );

  const memoizedValue = useMemo(
    () => ({
      languages,
      dispatch,
      getLanguageDetails,
      getLanguageCodeByName,
      fetchLanguages,
    }),
    [
      languages,
      dispatch,
      getLanguageDetails,
      getLanguageCodeByName,
      fetchLanguages,
    ],
  );

  return (
    <LanguagesContext.Provider value={memoizedValue}>
      {children}
    </LanguagesContext.Provider>
  );
};

export const useLanguages = () => useContext(LanguagesContext);
